import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Modal } from "@mui/material";
import {
  Close,
  AttachFile,
  ThumbUp,
  Send,
  RemoveRedEye,
  Done,
  DoneAll,
} from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import Styles from "./CombinedChatModal.module.css";
import { get_Document_By_id } from "../../../Services/ApiCalling/document";
import SocketService from "../../../commons/Socket/SocketService";
import FileByteView from "../../../commons/DocumentView/FileByteView";
import DownloadIcon from "@mui/icons-material/Download";

import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import {
  downloadChat,
  fileUpload,
  getBankingChatByName,
  getConsumerChatByName,
  getGeneratorChatByName,
} from "../../../Services/Chat/Chat";
import { toast } from "react-toastify";
import { formatDateTimeView } from "../../../commons/FormatDate";
import { useSelector } from "react-redux";
import { downloadChatHistory } from "../chatPdfGenerator";

// --- Custom Hook for Socket Logic ---

/**
 * Manages the WebSocket connection, message list, and event listeners for a chat.
 */
const useChatSocket = ({ chatId, fromUsername, toUsername, selfName }) => {
  const [messageList, setMessageList] = useState([]);

  // Memoize event handlers to stabilize the useEffect dependency array
  const handleMessage = useCallback((msg) => {
    setMessageList((prev) => [...prev, msg]);
  }, []);

  const handleChatHistory = useCallback((history) => {
    setMessageList(history);
  }, []);

  const handleMessageReceived = useCallback(({ messageId }) => {
    setMessageList((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)),
    );
  }, []);

  const handleMessageRead = useCallback(({ messageId }) => {
    setMessageList((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)),
    );
  }, []);

  // Effect to manage socket connection and listeners
  useEffect(() => {
    if (!chatId || !selfName) return;

    const socket = SocketService.getSocket();

    const selfUser = fromUsername === selfName ? fromUsername : toUsername;
    const otherUser = toUsername === selfName ? fromUsername : toUsername;

    // 1. Create and Join Room
    socket.emit("create-room", {
      chatId,
      fromUsername: selfUser,
      toUsername: otherUser,
    });
    socket.emit("join-room", {
      chatId,
      username: selfUser,
    });

    // 2. Register Listeners
    socket.on("message", handleMessage);
    socket.on("chat-history", handleChatHistory);
    socket.on("message-received", handleMessageReceived);
    socket.on("message-read", handleMessageRead);

    // 3. Cleanup on unmount or when chat changes
    return () => {
      socket.off("message", handleMessage);
      socket.off("chat-history", handleChatHistory);
      socket.off("message-received", handleMessageReceived);
      socket.off("message-read", handleMessageRead);
    };
  }, [
    chatId,
    fromUsername,
    toUsername,
    selfName,
    handleMessage,
    handleChatHistory,
    handleMessageReceived,
    handleMessageRead,
  ]);

  /**
   * Emits a message payload via the socket.
   */
  const sendMessage = useCallback(
    (payload) => {
      const socket = SocketService.getSocket();
      socket.emit("message", payload);
    },
    [], // SocketService.getSocket() is stable
  );

  return { messageList, sendMessage };
};

// --- Presentational Sub-Components ---

/**
 * Renders the list of available chats on the left.
 */
const ChatList = ({ data, selectedChatId, onSelect, selfName }) => (
  <div className={Styles.chat_mainList}>
    <div className={Styles.chat_mainHeading}>Chat</div>
    <div className={`scroll_bar_y ${Styles.chat_mainListItems}`}>
      {data?.map((ele) => {
        const chatUser =
          ele?.toUsername === selfName ? ele?.fromUsername : ele?.toUsername;
        return (
          <div
            key={ele?.chatId} // Added key prop
            className={`${Styles.Listing_Details} ${
              ele?.chatId === selectedChatId && Styles.selectedName
            }`}
            onClick={() => onSelect(ele)}
          >
            {chatUser}
          </div>
        );
      })}
    </div>
  </div>
);

/**
 * Renders the header of the active chat window.
 */
const ChatWindowHeader = ({ chatUser, onClose, handleDownload }) => (
  <div className={Styles.chat_mainHeading_right}>
    <div style={{ display: "flex", alignItems: "center" }}>
      <span>{chatUser}</span>
      <button
        onClick={() => {
          handleDownload();
        }}
        style={{ marginLeft: "10px" }}
      >
        <DownloadIcon />
      </button>
    </div>
    <Close onClick={onClose} style={{ cursor: "pointer" }} fontSize="small" />
  </div>
);

/**
 * Renders a single message bubble.
 */
const MessageBubble = ({ message, isSelf, onViewFile }) => {
  const downloadFileFromResponse = (response) => {
    // 1. Get the file URL from the response
    const fileUrl = response?.data;

    if (!fileUrl) {
      console.error(
        "Error: The response object does not contain a file URL in the 'data' field.",
      );
      return;
    }

    // 2. Helper to determine the file name from the URL path
    const getFileName = (url) => {
      try {
        // Use URL object to parse the path and remove query parameters
        const path = new URL(url).pathname;

        // Decode URI components to handle spaces and special characters (like %20)
        const decodedPath = decodeURIComponent(path);

        // Extract the filename (the part after the last '/')
        const fileNameWithQuery = decodedPath.substring(
          decodedPath.lastIndexOf("/") + 1,
        );

        // The provided URL contains a filename followed by an S3-specific query parameter.
        // Although the 'download' attribute handles it, cleaning it up here is safer.
        // Since the URL ends with .xlsx followed by query params, we need a good way to extract the filename.
        // For a well-formed S3 URL like this, we'll try to find the extension.

        // A more robust way: find the first occurrence of the file extension
        // before the AWS query params start (usually marked by '?')
        const parts = fileNameWithQuery.split("?");
        return parts[0].trim();
      } catch (e) {
        console.warn("Could not parse file name from URL, using default.");
        return "downloaded_file";
      }
    };

    const fileName = getFileName(fileUrl);

    // 3. Create a temporary anchor element (<a>) to trigger the download
    const link = document.createElement("a");

    // Set the file source URL
    link.href = fileUrl;

    // Set the 'download' attribute to suggest a filename and force download
    link.download = fileName;

    // Append to the document body (required for .click() to work consistently)
    document.body.appendChild(link);

    // 4. Programmatically click the link to start the download
    link.click();

    // 5. Clean up: remove the temporary element
    document.body.removeChild(link);
    console.log(`Download triggered for: ${fileName}`);
  };
  const onDownloadFile = async (fileUrl) => {
    let data = fileUrl?.split("/");
    let id = data?.[data?.length - 1];
    if (!id) throw new Error("Invalid file URL");
    let res = await get_Document_By_id({ type: "chat", id: id });
    downloadFileFromResponse(res);
    // saveAs(ListingTemplateBanking, "Listing Template.xlsx");
  };
  const isPDF = (url) => {
    console.debug("Checking if PDF for url:", url);
    if (!url) return false;
    // A simple check by file extension in the URL
    return url.toLowerCase().endsWith(".pdf");
  };
  const alignmentClass = isSelf ? Styles.right_allign : Styles.left_allign;
  const messageClass = isSelf
    ? Styles.right_allign_message
    : Styles.left_allign_message;

  return (
    <div className={Styles.chatMainElem}>
      <div className={`${Styles.commonText} ${alignmentClass}`}>
        <div className={messageClass}>
          <span>{message?.text}</span>
          {message?.fileUrl && (
            <>
              {isPDF(message?.text) ? (
                <>
                  <RemoveRedEye
                    onClick={() => onViewFile(message?.fileUrl, message?.text)}
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#f1935c",
                      marginLeft: "8px",
                    }}
                  />
                </>
              ) : (
                <>
                  <ArrowDownwardIcon
                    onClick={() => onDownloadFile(message.fileUrl)}
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#f1935c",
                      marginLeft: "8px",
                    }}
                    title="Download File"
                  />
                </>
              )}
            </>
          )}
          {isSelf && (
            <span style={{ marginLeft: "8px" }}>
              {message?.read ? (
                <DoneAll style={{ fontSize: "12px", color: "#34656d" }} />
              ) : (
                <Done style={{ fontSize: "12px" }} />
              )}
            </span>
          )}
        </div>
      </div>
      <div
        className={isSelf ? Styles.selfMessageTime : Styles.otherMessageTime}
      >
        {formatDateTimeView(message?.timestamp)}
      </div>
    </div>
  );
};

/**
 * Renders the list of messages.
 */
const MessageList = ({ messages, selfName, onViewFile, messagesEndRef }) => (
  <div className={`scroll_bar_y ${Styles.chatListContainer}`}>
    {messages?.map((ele, index) => (
      <MessageBubble
        key={ele.id || index} // Added key prop
        message={ele}
        isSelf={ele?.username === selfName}
        onViewFile={onViewFile}
      />
    ))}
    <div ref={messagesEndRef} />
  </div>
);

/**
 * Renders the chat input bar with send and attach buttons.
 */
// Make sure to import your Styles, AttachFile, Send, etc.

const ChatInput = ({
  message,
  setMessage,
  onSendText,
  onFileUpload,
  onSendOk,
}) => {
  // 1. Reference for the textarea
  const textareaRef = useRef(null);

  // 2. Auto-resize logic
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to shrink if text is deleted
      textareaRef.current.style.height = "auto";
      // Set height based on scrollHeight
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // 3. Handle Enter (Send) vs Shift+Enter (New Line)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent standard new line
      if (message.trim()) {
        onSendText();
      }
    }
  };

  return (
    <div
      className={Styles.inputMainContainer}
      // "alignItems: flex-end" ensures icons stay at the bottom when input grows up
      style={{ alignItems: "flex-end" }}
    >
      <div className={Styles.uploadIcons} style={{ paddingBottom: "10px" }}>
        <input
          type="file"
          id="fileInput"
          accept="application/pdf"
          onChange={onFileUpload}
          style={{ display: "none" }}
        />
        <AttachFile
          onClick={() => document.getElementById("fileInput").click()}
          style={{
            fontSize: "16px",
            color: "#f1935c",
            cursor: "pointer",
            paddingTop: "15px",
          }}
        />
        {/* <ThumbUp style={{ fontSize: "16px", color: "#525062" }} onClick={onSendOk} /> */}
      </div>

      {/* Replaced <input> with <textarea> */}
      <textarea
        ref={textareaRef}
        rows={1}
        value={message}
        placeholder="Type a Message....."
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{
          width: "100%",
          resize: "none",
          overflowY: "auto",

          // --- UPDATED STYLES FOR CENTERING ---
          minHeight: "45px", // Increased slightly for better touch target
          maxHeight: "45px", // Allow it to grow taller than 30px
          padding: "10px 0px", // 10px top/bottom padding centers the text
          lineHeight: "20px", // Explicit line height ensures stability
          boxSizing: "border-box", // Crucial: Includes padding in height calculation
          // ------------------------------------

          border: "none",
          outline: "none",
          backgroundColor: "transparent",
        }}
      />

      <div
        className={Styles.sendIconContianer}
        style={{ paddingBottom: "5px" }}
      >
        <Send
          style={{
            fontSize: "18px",
            color: "#525062",
            cursor: "pointer",
          }}
          onClick={() => onSendText()}
        />
      </div>
    </div>
  );
};

// --- Main Component ---

const CombinedChatModal = ({ open, handleClose, data, selfName }) => {
  const [selected, setSelected] = useState(data?.[0]);
  console.debug("selected1", selected);
  const [message, setMessage] = useState("");
  console.debug("message1", message);
  const [viewDoc, setViewDoc] = useState(false);
  const messagesEndRef = useRef(null);
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  // Use memoized values for chat participants
  const currentChatUser = useMemo(
    () =>
      selected?.toUsername === selfName
        ? selected?.fromUsername
        : selected?.toUsername,
    [selected, selfName],
  );

  const selfUsername = useMemo(
    () =>
      selected?.fromUsername === selfName
        ? selected?.fromUsername
        : selected?.toUsername,
    [selected, selfName],
  );

  // Use the custom hook to manage socket logic
  const { messageList, sendMessage: emitSocketMessage } = useChatSocket({
    chatId: selected?.chatId,
    fromUsername: selected?.fromUsername,
    toUsername: selected?.toUsername,
    selfName,
  });
  console.debug("messageList", selected, selfName);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messageList]);

  // --- Event Handlers ---

  /**
   * Sends a text message.
   */
  const handleSendText = () => {
    const text = message.trim();

    // 1. Validation
    // Using optional chaining (?.) on 'selected' prevents crashes if it's null
    if (!text || !selected?.chatId) return;

    // 2. Execution
    sendMessage(text);
    updateRoomDetails();

    // 3. UI Cleanup
    setMessage("");
  };

  // Helper: Handles the specific message payload
  const sendMessage = (text) => {
    emitSocketMessage({
      chatId: selected.chatId,
      username: selfUsername,
      text,
      fileUrl: null,
      fileType: null,
    });
  };

  // Helper: Handles room metadata updates
  const updateRoomDetails = async () => {
    const socket = SocketService.getSocket();
    let fromUsername = selfUsername;
    let toUsername =
      selected.toUsername === selfUsername
        ? selected.fromUsername
        : selected.toUsername;

    let payload = {
      chatId: selected.chatId,
      fromUserId: null,
      toUserId: null,
    };
    if (role === "CONSUMER") {
      if (fromUsername?.includes("C")) {
        let self = await getConsumerChatByName(fromUsername);
        if (self?.data?.code === 200) {
          payload["fromUserId"] = self?.data?.data?.userId;
        }

        let counter = await getGeneratorChatByName(toUsername);
        if (counter?.data?.code === 200) {
          payload["toUserId"] = counter?.data?.data?.userId;
        }
      } else {
        let self = await getGeneratorChatByName(fromUsername);
        if (self?.data?.code === 200) {
          payload["fromUserId"] = self?.data?.data?.userId;
        }

        let counter = await getConsumerChatByName(toUsername);
        if (counter?.data?.code === 200) {
          payload["toUserId"] = counter?.data?.data?.userId;
        }
      }
    } else if (role === "GENERATOR") {
      if (fromUsername?.includes("G")) {
        let self = await getGeneratorChatByName(fromUsername);
        if (self?.data?.code === 200) {
          payload["fromUserId"] = self?.data?.data?.userId;
        }

        let counter = await getConsumerChatByName(toUsername);
        if (counter?.data?.code === 200) {
          payload["toUserId"] = counter?.data?.data?.userId;
        }
      } else {
        let self = await getConsumerChatByName(fromUsername);
        if (self?.data?.code === 200) {
          payload["fromUserId"] = self?.data?.data?.userId;
        }

        let counter = await getGeneratorChatByName(toUsername);
        if (counter?.data?.code === 200) {
          payload["toUserId"] = counter?.data?.data?.userId;
        }
      }
    } else {
      let self = await getBankingChatByName(fromUsername);
      if (self?.data?.code === 200) {
        payload["fromUserId"] = self?.data?.data?.userId;
      }

      let counter = await getBankingChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["toUserId"] = counter?.data?.data?.userId;
      }
    }

    socket.emit("create-room", payload);
  };

  /**
   * Handles file selection, validation, upload, and sending.
   */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const acceptedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    // Validation: Check if the file type is NOT included in the acceptedTypes array
    if (!acceptedTypes.includes(file.type)) {
      ErrorMessage("Only PDF and Excel (.xlsx) files are accepted.");
      e.target.value = ""; // Reset the input
      return;
    }

    if (selected?.chatId) {
      try {
        const formData = new FormData();
        formData.append("file", file);

        let res = await fileUpload(formData, selected.chatId);

        if (res?.data) {
          emitSocketMessage({
            chatId: selected.chatId,
            username: selfUsername,
            text: file.name,
            fileUrl: res.data,
            fileType: file.type,
          });
          updateRoomDetails();
        }
      } catch (error) {
        ErrorMessage("File upload failed. Please try again.");
        console.error("File upload error:", error);
      }
    }

    e.target.value = ""; // Reset input
  };

  /**
   * Fetches and displays a document.
   */
  const handleViewFile = async (fileUrl, name) => {
    try {
      let data = fileUrl?.split("/");
      let id = data?.[data?.length - 1];
      if (!id) throw new Error("Invalid file URL");

      let res = await get_Document_By_id({ type: "chat", id: id });
      if (!res?.data) {
        throw new Error("Failed to fetch document");
      }
      setViewDoc({ url: res.data, name: name });
    } catch (error) {
      ErrorMessage(error.message || "Failed to load document.");
      console.error("View file error:", error);
    }
  };

  const handleDownload = async (
    messages = messageList,
    fromUsername = selected?.fromUsername,
    toUsername = selected?.toUsername,
  ) => {
    let payload = {
      selfListing: null,
      selfCompany: null,
      selfEntity: null,
      counterListing: null,
      counterCompany: null,
      counterEntity: null,
    };
    if (role === "CONSUMER") {
      let from = fromUsername?.includes("C") ? fromUsername : toUsername;
      let to = fromUsername?.includes("G") ? fromUsername : toUsername;
      let self = await getConsumerChatByName(from);
      if (self?.data?.code === 200) {
        payload["selfListing"] = self?.data?.data?.name;
        payload["selfEntity"] = self?.data?.data?.consumerName;
        payload["selfCompany"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
      }

      let counter = await getGeneratorChatByName(to);
      if (counter?.data?.code === 200) {
        payload["counterListing"] = counter?.data?.data?.name;
        payload["counterEntity"] = counter?.data?.data?.generatorName;
        payload["counterCompany"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
      }
    } else if (role === "GENERATOR") {
      let from = fromUsername?.includes("G") ? fromUsername : toUsername;
      let to = fromUsername?.includes("C") ? fromUsername : toUsername;
      let self = await getGeneratorChatByName(from);
      if (self?.data?.code === 200) {
        payload["selfListing"] = self?.data?.data?.name;
        payload["selfEntity"] = self?.data?.data?.generatorName;
        payload["selfCompany"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
      }

      let counter = await getConsumerChatByName(to);
      if (counter?.data?.code === 200) {
        payload["counterListing"] = counter?.data?.data?.name;
        payload["counterEntity"] = counter?.data?.data?.consumerName;
        payload["counterCompany"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
      }
    } else {
      let self = await getBankingChatByName(fromUsername);
      if (self?.data?.code === 200) {
        console.debug(" self?.data?.data", self?.data?.data);
        payload["selfListing"] = self?.data?.data?.name;
        payload["selfEntity"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
        payload["selfCompany"] =
          self?.data?.data?.companyName || self?.data?.data?.companyDisplayName;
      }

      let counter = await getBankingChatByName(toUsername);
      if (counter?.data?.code === 200) {
        payload["counterListing"] = counter?.data?.data?.name;
        payload["counterEntity"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
        payload["counterCompany"] =
          counter?.data?.data?.companyName ||
          counter?.data?.data?.companyDisplayName;
      }
    }
    downloadChatHistory(messages, fromUsername, payload);
  };
  const handleSendOk = () => {
    emitSocketMessage({
      chatId: selected.chatId,
      username: selfUsername,
      text: "OK",
      fileUrl: null,
      fileType: null,
    });
  };

  return (
    <>
      {viewDoc && (
        <FileByteView open={viewDoc} handleClose={() => setViewDoc(false)} />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="combined-chat-modal"
      >
        <div className={` ${Styles.chat_main_modal}`}>
          <ChatList
            data={data}
            selectedChatId={selected?.chatId}
            onSelect={setSelected}
            selfName={selfName}
          />

          <div className={Styles.chat_mainDetails}>
            <div>
              <ChatWindowHeader
                chatUser={currentChatUser}
                onClose={handleClose}
                handleDownload={handleDownload}
              />
              <MessageList
                messages={messageList}
                selfName={selfName}
                onViewFile={handleViewFile}
                messagesEndRef={messagesEndRef}
              />
              <ChatInput
                message={message}
                setMessage={setMessage}
                onSendText={handleSendText}
                onFileUpload={handleFileUpload}
                onSendOk={handleSendOk}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CombinedChatModal;
